import { Eye, Loader2, RefreshCw } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { listApplications } from "../services/api/applicationsApi";
import type { Application, ApplicationStatus } from "../types/onboarding";
import { STATUS_COLORS, STATUS_LABELS } from "../types/onboarding";

const STATUS_TABS: (ApplicationStatus | "all")[] = [
	"all",
	"submitted",
	"under_review",
	"approved",
	"rejected",
	"approval_failed",
];

const Applications: React.FC = () => {
	const navigate = useNavigate();
	const [applications, setApplications] = useState<Application[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "all">(
		"all",
	);
	const [loading, setLoading] = useState(true);
	const limit = 20;

	const fetchApplications = useCallback(async () => {
		setLoading(true);
		try {
			const params: Record<string, unknown> = { page, limit };
			if (statusFilter !== "all") params.status = statusFilter;
			const result = await listApplications(
				params as { status?: string; page?: number; limit?: number },
			);
			setApplications(result.data);
			setTotal(result.pagination.total);
		} catch (err) {
			console.error("Failed to load applications:", err);
		} finally {
			setLoading(false);
		}
	}, [page, statusFilter]);

	useEffect(() => {
		fetchApplications();
	}, [fetchApplications]);

	const totalPages = Math.ceil(total / limit);

	return (
		<div
			style={{
				padding: "1.5rem",
				paddingTop: "5rem",
				maxWidth: "80rem",
				margin: "0 auto",
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "1.5rem",
				}}
			>
				<h1
					style={{
						fontSize: "1.5rem",
						fontWeight: 600,
						color: "var(--color-text-primary)",
					}}
				>
					Onboarding Applications
				</h1>
				<Button
					variant="outline"
					onClick={fetchApplications}
					disabled={loading}
				>
					<RefreshCw
						size={14}
						style={{
							marginRight: "0.35rem",
							...(loading ? { animation: "spin 1s linear infinite" } : {}),
						}}
					/>
					{loading ? "Loading..." : "Refresh"}
				</Button>
			</div>

			{/* Status Tabs */}
			<div
				style={{
					display: "flex",
					gap: "0.25rem",
					marginBottom: "1rem",
					borderBottom: "1px solid var(--color-border)",
					paddingBottom: "0",
				}}
			>
				{STATUS_TABS.map((status) => (
					<button
						key={status}
						type="button"
						onClick={() => {
							setStatusFilter(status);
							setPage(1);
						}}
						style={{
							padding: "0.5rem 1rem",
							fontSize: "0.8rem",
							fontWeight: statusFilter === status ? 600 : 400,
							color:
								statusFilter === status
									? "var(--color-primary)"
									: "var(--color-text-secondary)",
							background: "none",
							border: "none",
							borderBottom:
								statusFilter === status
									? "2px solid var(--color-primary)"
									: "2px solid transparent",
							cursor: "pointer",
							textTransform: "capitalize",
						}}
					>
						{status === "all" ? "All" : STATUS_LABELS[status]}
					</button>
				))}
			</div>

			{/* Table */}
			{loading ? (
				<div
					style={{ display: "flex", justifyContent: "center", padding: "3rem" }}
				>
					<Loader2
						size={24}
						style={{
							animation: "spin 1s linear infinite",
							color: "var(--color-primary)",
						}}
					/>
				</div>
			) : applications.length === 0 ? (
				<div
					style={{
						textAlign: "center",
						padding: "3rem",
						color: "var(--color-text-secondary)",
					}}
				>
					No applications found.
				</div>
			) : (
				<div style={{ overflowX: "auto" }}>
					<table
						style={{
							width: "100%",
							borderCollapse: "collapse",
							fontSize: "0.85rem",
						}}
					>
						<thead>
							<tr style={{ borderBottom: "2px solid var(--color-border)" }}>
								<th
									style={{
										textAlign: "left",
										padding: "0.75rem 0.5rem",
										color: "var(--color-text-secondary)",
										fontWeight: 500,
									}}
								>
									Company
								</th>
								<th
									style={{
										textAlign: "left",
										padding: "0.75rem 0.5rem",
										color: "var(--color-text-secondary)",
										fontWeight: 500,
									}}
								>
									Contact
								</th>
								<th
									style={{
										textAlign: "left",
										padding: "0.75rem 0.5rem",
										color: "var(--color-text-secondary)",
										fontWeight: 500,
									}}
								>
									Status
								</th>
								<th
									style={{
										textAlign: "left",
										padding: "0.75rem 0.5rem",
										color: "var(--color-text-secondary)",
										fontWeight: 500,
									}}
								>
									Submitted
								</th>
								<th
									style={{
										textAlign: "right",
										padding: "0.75rem 0.5rem",
										color: "var(--color-text-secondary)",
										fontWeight: 500,
									}}
								>
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{applications.map((app) => (
								<tr
									key={app.id}
									style={{
										borderBottom: "1px solid var(--color-border)",
										cursor: "pointer",
									}}
									onClick={() => navigate(`/applications/${app.id}`)}
								>
									<td
										style={{
											padding: "0.75rem 0.5rem",
											color: "var(--color-text-primary)",
											fontWeight: 500,
										}}
									>
										{app.company_name || "\u2014"}
									</td>
									<td
										style={{
											padding: "0.75rem 0.5rem",
											color: "var(--color-text-secondary)",
										}}
									>
										{app.primary_contact?.email || "\u2014"}
									</td>
									<td style={{ padding: "0.75rem 0.5rem" }}>
										<span
											style={{
												display: "inline-block",
												padding: "0.15rem 0.5rem",
												borderRadius: "1rem",
												fontSize: "0.75rem",
												fontWeight: 500,
												color: STATUS_COLORS[app.status],
												background: `color-mix(in srgb, ${STATUS_COLORS[app.status]} 12%, transparent)`,
											}}
										>
											{STATUS_LABELS[app.status]}
										</span>
									</td>
									<td
										style={{
											padding: "0.75rem 0.5rem",
											color: "var(--color-text-secondary)",
										}}
									>
										{(() => {
											const date = app.submitted_at || app.created_at;
											return date
												? new Date(date).toLocaleDateString()
												: "\u2014";
										})()}
									</td>
									<td style={{ padding: "0.75rem 0.5rem", textAlign: "right" }}>
										<Button
											variant="ghost"
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/applications/${app.id}`);
											}}
										>
											<Eye size={14} style={{ marginRight: "0.25rem" }} />
											View
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Pagination */}
			{totalPages > 1 && (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "0.5rem",
						marginTop: "1.5rem",
					}}
				>
					<Button
						variant="outline"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
					>
						Previous
					</Button>
					<span
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "0.85rem",
							color: "var(--color-text-secondary)",
							padding: "0 0.75rem",
						}}
					>
						Page {page} of {totalPages}
					</span>
					<Button
						variant="outline"
						disabled={page >= totalPages}
						onClick={() => setPage((p) => p + 1)}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	);
};

export default Applications;
